import {
  useEffect, useState, memo, useMemo, useCallback, useRef
} from 'react';
import PropTypes from 'prop-types';
import {
  Box,
  Button,
  Card,
  Table,
  TableBody,
  TableCell,
  TableRow,
  Typography,
  useTheme,
  useMediaQuery,
  Grid,
  Divider,
  Tooltip,
  IconButton,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Stack
} from '@mui/material';
import EditOutlinedIcon from '@mui/icons-material/EditOutlined';
import VisibilityOutlinedIcon from '@mui/icons-material/VisibilityOutlined';
import { useHistory } from 'react-router';
import ROUTE from 'src/utils/routeConstants';
import { useDispatch, useSelector } from 'react-redux';
import { TRANSACTION_TYPES, USER_ROLES } from 'src/utils/constants';
import {
  getTransactionList,
} from 'src/redux/transactionSlice';
import { getAllVehicles } from 'src/redux/vehicleSlice';
import { getAllDispensers } from 'src/redux/dispenserSlice';
import Loader from 'src/common/Loader';
import EnhancedTableHead, { getComparator } from 'src/common/EnhancedTableHead';
import {
  TABLE_ACTION_ICON_SX
} from 'src/common/tableActionStyles';
import { transactionTableHeaders } from 'src/utils/tableHeaders';
import {
  getFormattedDate,
  getFuelQuantity,
  getTransactionQueryParams,
  hasAdminLikeAccess,
  removeLeadingZeros,
} from 'src/utils/getInitials';
import useAuth from 'src/context/AuthContext';
import { useTableColumnPreferences } from 'src/common/TableColumnVisibilityMenu';
import { APIService } from 'src/api/api';
import { getUnreadNotificationsCount } from 'src/redux/notificationSlice';
import SiteDetailsDialog from 'src/components/cards/SiteDetailsDialog';
import VehicleDetailsDialog from 'src/components/cards/VehicleDetailsDialog';
import { getDispenserDisplayValue } from './transactionService';
import DispenserDetailsDialog from './DispenserDetailsDialog';
import UpdateOdometerModal from './formModel/updateOdometerModal';
import NewTransaction from './NewTransaction';
import { TRANSACTION_STYLE_COLORS as VEHICLE_STYLE_COLORS } from './transactionStyleTokens';

const getTransactionSymbolSx = () => ({
  width: 32,
  height: 32,
  borderRadius: '6px',
  color: VEHICLE_STYLE_COLORS.primary,
  backgroundColor: 'rgba(11, 110, 153, 0.08)',
  border: '1px solid rgba(11, 110, 153, 0.28)',
  transition: 'all 0.2s ease-in-out',
  '&:hover': {
    color: VEHICLE_STYLE_COLORS.primary,
    backgroundColor: 'rgba(11, 110, 153, 0.12)',
    borderColor: 'rgba(11, 110, 153, 0.18)',
    filter: 'none'
  },
  '&.Mui-disabled': {
    color: 'rgba(11, 110, 153, 0.3)',
    backgroundColor: 'transparent',
    borderColor: 'transparent'
  }
});

const DEFAULT_TRANSACTION_COLUMN_WIDTH = 140;
const TRANSACTION_COLUMN_WIDTHS = {
  action: 100,
  serial_number: 64,
  address: 190,
  delivery_date: 170,
  end_time: 170,
  order_date: 170,
  site_name: 220,
  start_time: 220,
  vehicle_category: 160,
  vehicle_model: 160,
  vehicle_number: 150,
};

const getTransactionTableMinWidth = (columns, isTablet) => {
  const columnWidth = columns.reduce(
    (total, column) => total + (TRANSACTION_COLUMN_WIDTHS[column.id] || DEFAULT_TRANSACTION_COLUMN_WIDTH),
    0
  );
  return Math.max(isTablet ? 900 : 1100, columnWidth);
};

const TRANSACTION_PAGE_SIZES = [10, 25, 50];

export const TransactionPagination = ({
  page, pageSize, count, onPageChange, onPageSizeChange
}) => {
  const pageCount = Math.max(1, Math.ceil(count / pageSize));
  const pageNumbers = pageCount <= 7
    ? Array.from({ length: pageCount }, (_, index) => index + 1)
    : [...new Set([1, pageCount, page, page + 1, page + 2].filter((item) => item >= 1 && item <= pageCount))].sort((a, b) => a - b);
  const from = count === 0 ? 0 : (page * pageSize) + 1;
  const to = Math.min((page + 1) * pageSize, count);
  const pageButtonSx = {
    minWidth: 36,
    height: 36,
    px: 1.25,
    borderRadius: 1,
    textTransform: 'none',
    color: VEHICLE_STYLE_COLORS.text,
    backgroundColor: 'transparent',
    '&:hover': { backgroundColor: VEHICLE_STYLE_COLORS.hoverControl },
    '&[aria-current="page"]': {
      color: VEHICLE_STYLE_COLORS.white,
      backgroundColor: 'hsl(198 87% 32%)',
      borderColor: 'hsl(198 87% 32%)',
      '&:hover': { backgroundColor: 'hsl(198 87% 32%)', borderColor: 'hsl(198 87% 32%)' }
    }
  };
  const navigationButtonSx = {
    ...pageButtonSx,
    border: `1px solid ${VEHICLE_STYLE_COLORS.border}`,
    backgroundColor: VEHICLE_STYLE_COLORS.white,
    color: VEHICLE_STYLE_COLORS.text,
    '&:hover': {
      backgroundColor: 'rgba(11, 106, 153, 0.06)',
      borderColor: 'hsl(198 87% 32%)',
      color: 'hsl(198 87% 32%)'
    },
    '&.Mui-disabled': {
      color: VEHICLE_STYLE_COLORS.subtle,
      borderColor: VEHICLE_STYLE_COLORS.border
    }
  };

  return (
    <Box sx={{
      display: 'flex',
      flexDirection: { xs: 'column', sm: 'row' },
      alignItems: { xs: 'stretch', sm: 'center' },
      justifyContent: 'space-between',
      gap: 2,
      px: 3,
      py: 2,
      borderTop: `1px solid ${VEHICLE_STYLE_COLORS.separator}`,
      bgcolor: VEHICLE_STYLE_COLORS.white
    }}
    >
      <Stack direction="row" alignItems="center" spacing={2} flexWrap="wrap">
        <Typography variant="body2" sx={{ color: VEHICLE_STYLE_COLORS.muted }}>{count ? `${from}–${to} of ${count}` : '0 transactions'}</Typography>
        <Stack direction="row" spacing={1} alignItems="center">
          <Typography variant="caption" sx={{ color: VEHICLE_STYLE_COLORS.muted }}>Per page</Typography>
          <FormControl
            size="small"
            sx={{
              minWidth: 82,
              '& .MuiOutlinedInput-root': {
                color: VEHICLE_STYLE_COLORS.text,
                backgroundColor: VEHICLE_STYLE_COLORS.white,
                '& fieldset': { borderColor: VEHICLE_STYLE_COLORS.border },
                '&:hover fieldset': { borderColor: VEHICLE_STYLE_COLORS.subtle },
                '&.Mui-focused fieldset': { borderColor: 'hsl(198 87% 32%)' }
              },
              '& .MuiInputLabel-root': { color: VEHICLE_STYLE_COLORS.muted },
              '& .MuiSvgIcon-root': { color: VEHICLE_STYLE_COLORS.muted }
            }}
          >
            <InputLabel id="transaction-page-size-label">Rows</InputLabel>
            <Select labelId="transaction-page-size-label" value={pageSize} label="Rows" onChange={onPageSizeChange}>
              {TRANSACTION_PAGE_SIZES.map((size) => <MenuItem key={size} value={size}>{size}</MenuItem>)}
            </Select>
          </FormControl>
        </Stack>
      </Stack>
      <Stack
        direction="row"
        spacing={0.75}
        justifyContent={{ xs: 'center', sm: 'flex-end' }}
        alignItems="center"
      >
        <Button sx={navigationButtonSx} disabled={page <= 0} onClick={() => onPageChange(null, page - 1)}>Previous</Button>
        {pageNumbers.map((number, index) => (
          <Box key={number} sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
            {index > 0 && pageNumbers[index - 1] !== number - 1 && <Typography sx={{ color: VEHICLE_STYLE_COLORS.subtle }}>…</Typography>}
            <Button
              aria-current={number === page + 1 ? 'page' : undefined}
              sx={{
                ...pageButtonSx,
                ...(number === page + 1 ? {
                  bgcolor: VEHICLE_STYLE_COLORS.primary,
                  color: VEHICLE_STYLE_COLORS.white,
                  '&:hover': { bgcolor: VEHICLE_STYLE_COLORS.primary }
                } : {})
              }}
              onClick={() => onPageChange(null, number - 1)}
            >
              {number}
            </Button>
          </Box>
        ))}
        <Button sx={navigationButtonSx} disabled={page >= pageCount - 1} onClick={() => onPageChange(null, page + 1)}>Next</Button>
      </Stack>
    </Box>
  );
};

TransactionPagination.propTypes = {
  page: PropTypes.number.isRequired,
  pageSize: PropTypes.number.isRequired,
  count: PropTypes.number.isRequired,
  onPageChange: PropTypes.func.isRequired,
  onPageSizeChange: PropTypes.func.isRequired,
};

const getRelatedEntityId = (value) => {
  if (!value || typeof value !== 'object') return value;
  return value.id ?? value.vehicle_id ?? value.dispenser_id ?? value.site_id;
};

const getVehicleDisplayValue = (transaction = {}) => (
  transaction.vehicle_number
  || transaction.vehicle?.vehicle_number
  || transaction.vehicle?.registration_num
  || transaction.vehicle?.number
  || 'N/A'
);

const EntityValue = ({
  value, entityId, entityType, onOpen, variant = 'body2', sx
}) => {
  const displayValue = value || 'N/A';
  const isClickable = entityId !== null && entityId !== undefined && displayValue !== 'N/A';
  const text = (
    <Typography
      variant={variant}
      sx={{
        color: 'inherit',
        fontWeight: 500,
        overflow: 'hidden',
        textOverflow: 'ellipsis',
        whiteSpace: 'nowrap',
        textAlign: 'center !important',
        ...sx,
      }}
    >
      {displayValue}
    </Typography>
  );

  if (!isClickable) return text;

  return (
    <Box
      component="button"
      type="button"
      onClick={(event) => {
        event.stopPropagation();
        onOpen(entityType, entityId, displayValue);
      }}
      sx={{
        background: 'transparent',
        border: 0,
        color: 'inherit',
        cursor: 'pointer',
        display: 'inline-block',
        width: '100%',
        maxWidth: '100%',
        m: 0,
        p: 0,
        textAlign: 'center !important',
        '&:hover': { textDecoration: 'underline' },
        '&:focus-visible': { outline: '2px solid', outlineColor: 'primary.main', outlineOffset: 2 },
      }}
    >
      {text}
    </Box>
  );
};

EntityValue.propTypes = {
  value: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  entityId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  entityType: PropTypes.oneOf(['site', 'vehicle', 'dispenser']).isRequired,
  onOpen: PropTypes.func.isRequired,
  variant: PropTypes.string,
  sx: PropTypes.object,
};

// Mobile Card View Component for Purchase Transactions
const PurchaseMobileCard = memo(({
  transaction, onViewClick, onEditClick, onEntityClick, theme, disabled, canEdit = true
}) => (
  <Card
    sx={{
      mb: 2,
      p: { xs: 2, sm: 2.5 },
      borderRadius: 3,
      background: VEHICLE_STYLE_COLORS.white,
      boxShadow: '0 2px 8px rgba(0,0,0,0.08)',
      border: `1px solid ${VEHICLE_STYLE_COLORS.border}`,
      transition: 'all 0.3s ease',
      '&:hover': {
        transform: 'translateY(-2px)',
        boxShadow: '0 4px 12px rgba(0,0,0,0.12)',
      },
    }}
  >
    {/* Header Section */}
    <Box
      sx={{
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'flex-start',
        mb: 2,
      }}
    >
      <Box sx={{ flex: 1 }}>
        <Typography
          variant="h6"
          sx={{
            fontWeight: 700,
            color: VEHICLE_STYLE_COLORS.primary,
            fontSize: { xs: '1rem', sm: '1.125rem' },
            mb: 0.5,
          }}
        >
          ID:
          {' '}
          {transaction.id}
        </Typography>
        <Box sx={{ color: theme.palette.text.secondary, fontSize: { xs: '0.813rem', sm: '0.875rem' } }}>
          <EntityValue
            value={transaction.site_name}
            entityId={getRelatedEntityId(transaction.site_id ?? transaction.site)}
            entityType="site"
            onOpen={onEntityClick}
          />
        </Box>
      </Box>
    </Box>

    <Divider sx={{ mb: 2 }} />

    {/* Dates Grid */}
    <Grid container spacing={2} sx={{ mb: 2 }}>
      <Grid item xs={6}>
        <Box>
          <Typography
            variant="caption"
            sx={{
              color: theme.palette.text.secondary,
              fontWeight: 600,
              display: 'block',
              mb: 0.5,
            }}
          >
            Order Date
          </Typography>
          <Typography
            variant="body2"
            sx={{ color: theme.palette.text.primary, fontWeight: 500 }}
          >
            {getFormattedDate(transaction.order_date) || 'N/A'}
          </Typography>
        </Box>
      </Grid>

      <Grid item xs={6}>
        <Box>
          <Typography
            variant="caption"
            sx={{
              color: theme.palette.text.secondary,
              fontWeight: 600,
              display: 'block',
              mb: 0.5,
            }}
          >
            Delivery Date
          </Typography>
          <Typography
            variant="body2"
            sx={{ color: theme.palette.text.primary, fontWeight: 500 }}
          >
            {getFormattedDate(transaction.delivery_date) || 'N/A'}
          </Typography>
        </Box>
      </Grid>

      <Grid item xs={12} sm={6}>
        <Box>
          <Typography
            variant="caption"
            sx={{
              color: theme.palette.text.secondary,
              fontWeight: 600,
              display: 'block',
              mb: 0.5,
            }}
          >
            Invoice Number
          </Typography>
          <Typography
            variant="body2"
            sx={{ color: theme.palette.text.primary, fontWeight: 500 }}
          >
            {transaction.invoice_number || 'N/A'}
          </Typography>
        </Box>
      </Grid>

      <Grid item xs={12} sm={6}>
        <Box>
          <Typography
            variant="caption"
            sx={{
              color: theme.palette.text.secondary,
              fontWeight: 600,
              display: 'block',
              mb: 0.5,
            }}
          >
            Supplier
          </Typography>
          <Typography
            variant="body2"
            sx={{ color: theme.palette.text.primary, fontWeight: 500 }}
          >
            {transaction.supplier || 'N/A'}
          </Typography>
        </Box>
      </Grid>

      <Grid item xs={12}>
        <Box>
          <Typography
            variant="caption"
            sx={{
              color: theme.palette.text.secondary,
              fontWeight: 600,
              display: 'block',
              mb: 0.5,
            }}
          >
            Received Tank
          </Typography>
          <Typography
            variant="body2"
            sx={{ color: theme.palette.text.primary, fontWeight: 500 }}
          >
            {transaction.received_tank_name || 'N/A'}
          </Typography>
        </Box>
      </Grid>
    </Grid>

    {/* Quantity & Price Stats */}
    <Grid container spacing={2} sx={{ mb: 2 }}>
      <Grid item xs={6}>
        <Box
          sx={{
            textAlign: 'center',
            p: 1.5,
            borderRadius: 2,
            bgcolor: VEHICLE_STYLE_COLORS.selected,
          }}
        >
          <Typography
            variant="caption"
            sx={{
              color: theme.palette.text.secondary,
              fontWeight: 600,
              display: 'block',
              mb: 0.5,
            }}
          >
            Quantity
          </Typography>
          <Typography
            variant="h6"
            sx={{
              fontWeight: 700,
              color: VEHICLE_STYLE_COLORS.primary,
              fontSize: { xs: '1.125rem', sm: '1.25rem' },
            }}
          >
            {getFuelQuantity(
              transaction.actual_filled || transaction.quantity,
              transaction.actual_filled_gallons || transaction.quantity_gallons
            ) || '0 L'}
          </Typography>
        </Box>
      </Grid>
      <Grid item xs={6}>
        <Box
          sx={{
            textAlign: 'center',
            p: 1.5,
            borderRadius: 2,
            bgcolor: theme.palette.success.lighter || 'rgba(46, 125, 50, 0.08)',
          }}
        >
          <Typography
            variant="caption"
            sx={{
              color: theme.palette.text.secondary,
              fontWeight: 600,
              display: 'block',
              mb: 0.5,
            }}
          >
            Price/Litre
          </Typography>
          <Typography
            variant="h6"
            sx={{
              fontWeight: 700,
              color: theme.palette.success.main,
              fontSize: { xs: '1.125rem', sm: '1.25rem' },
            }}
          >
            ₹
            {transaction.price_per_litre || '0'}
          </Typography>
        </Box>
      </Grid>
    </Grid>

    {/* Action Buttons */}
    <Box sx={{ display: 'flex', gap: 1.5, mt: 2 }}>
      <Button
        variant="outlined"
        fullWidth
        disabled={disabled}
        startIcon={<VisibilityOutlinedIcon />}
        sx={{
          borderRadius: 2,
          textTransform: 'none',
          py: { xs: 1, sm: 1.25 },
          fontSize: { xs: '0.875rem', sm: '0.938rem' },
          fontWeight: 600,
          borderColor: 'hsl(198 87% 32%)',
          color: 'hsl(198 87% 32%)',
          '&:hover': {
            borderColor: 'hsl(198 87% 32%)',
            backgroundColor: 'rgba(11, 106, 153, 0.06)',
          },
        }}
        onClick={() => onViewClick(transaction)}
      >
        View
      </Button>
      {canEdit && (
        <Button
          variant="contained"
          fullWidth
          disabled={disabled}
          startIcon={<EditOutlinedIcon />}
          sx={{
            borderRadius: 2,
            textTransform: 'none',
            py: { xs: 1, sm: 1.25 },
            fontSize: { xs: '0.875rem', sm: '0.938rem' },
            fontWeight: 600,
            backgroundColor: 'hsl(198 87% 32%)',
            borderColor: 'hsl(198 87% 32%)',
            boxShadow: '0 2px 8px rgba(0,0,0,0.15)',
            '&:hover': {
              backgroundColor: 'hsl(198 87% 32%)',
              borderColor: 'hsl(198 87% 32%)',
              filter: 'brightness(0.95)',
              boxShadow: '0 4px 12px rgba(0,0,0,0.2)',
            },
          }}
          onClick={() => onEditClick(transaction)}
        >
          Edit
        </Button>
      )}
    </Box>
  </Card>
));

PurchaseMobileCard.displayName = 'PurchaseMobileCard';

PurchaseMobileCard.propTypes = {
  transaction: PropTypes.object.isRequired,
  onViewClick: PropTypes.func.isRequired,
  onEditClick: PropTypes.func.isRequired,
  onEntityClick: PropTypes.func.isRequired,
  theme: PropTypes.object.isRequired,
  disabled: PropTypes.bool,
  canEdit: PropTypes.bool,
};

// Mobile Card View Component for Sales Transactions
const SalesMobileCard = memo(({
  transaction, onViewClick, onEditClick, onEntityClick, theme, disabled, canEdit = true
}) => (
  <Card
    sx={{
      mb: 2,
      p: { xs: 2, sm: 2.5 },
      borderRadius: 3,
      background: VEHICLE_STYLE_COLORS.white,
      boxShadow: '0 2px 8px rgba(0,0,0,0.08)',
      border: `1px solid ${VEHICLE_STYLE_COLORS.border}`,
      transition: 'all 0.3s ease',
      '&:hover': {
        transform: 'translateY(-2px)',
        boxShadow: '0 4px 12px rgba(0,0,0,0.12)',
      },
    }}
  >
    {/* Header Section */}
    <Box
      sx={{
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'flex-start',
        mb: 2,
      }}
    >
      <Box sx={{ flex: 1 }}>
        <Typography
          variant="h6"
          sx={{
            fontWeight: 700,
            color: VEHICLE_STYLE_COLORS.primary,
            fontSize: { xs: '1rem', sm: '1.125rem' },
            mb: 0.5,
          }}
        >
          Order:
          {' '}
          {transaction.order_id || 'N/A'}
        </Typography>
        <Typography
          variant="body2"
          sx={{
            color: theme.palette.text.secondary,
            fontSize: { xs: '0.813rem', sm: '0.875rem' },
          }}
        >
          {getFormattedDate(transaction.start_time) || 'N/A'}
        </Typography>
      </Box>
    </Box>

    <Divider sx={{ mb: 2 }} />

    {/* Transaction Details */}
    <Grid container spacing={2} sx={{ mb: 2 }}>
      <Grid item xs={12}>
        <Box>
          <Typography
            variant="caption"
            sx={{
              color: theme.palette.text.secondary,
              fontWeight: 600,
              display: 'block',
              mb: 0.5,
            }}
          >
            Site & Location
          </Typography>
          <EntityValue
            value={transaction.site_name}
            entityId={getRelatedEntityId(transaction.site_id ?? transaction.site)}
            entityType="site"
            onOpen={onEntityClick}
            sx={{ color: theme.palette.text.primary, mb: 0.5 }}
          />
          <EntityValue
            value={getDispenserDisplayValue(transaction)}
            entityId={getRelatedEntityId(transaction.dispenser_id ?? transaction.dispenser)}
            entityType="dispenser"
            onOpen={onEntityClick}
            variant="caption"
            sx={{ color: theme.palette.text.secondary }}
          />
        </Box>
      </Grid>

      <Grid item xs={6} sm={4}>
        <Box>
          <Typography
            variant="caption"
            sx={{
              color: theme.palette.text.secondary,
              fontWeight: 600,
              display: 'block',
              mb: 0.5,
            }}
          >
            Nozzle
          </Typography>
          <Typography
            variant="body2"
            sx={{ color: theme.palette.text.primary, fontWeight: 500 }}
          >
            {transaction.nozzle_num || 'N/A'}
          </Typography>
        </Box>
      </Grid>

      <Grid item xs={6} sm={4}>
        <Box>
          <Typography
            variant="caption"
            sx={{
              color: theme.palette.text.secondary,
              fontWeight: 600,
              display: 'block',
              mb: 0.5,
            }}
          >
            Card 1
          </Typography>
          <Typography
            variant="body2"
            sx={{ color: theme.palette.text.primary, fontWeight: 500 }}
          >
            {removeLeadingZeros(transaction.card_num_1) || 'N/A'}
          </Typography>
        </Box>
      </Grid>

      <Grid item xs={6} sm={4}>
        <Box>
          <Typography
            variant="caption"
            sx={{
              color: theme.palette.text.secondary,
              fontWeight: 600,
              display: 'block',
              mb: 0.5,
            }}
          >
            Card 2
          </Typography>
          <Typography
            variant="body2"
            sx={{ color: theme.palette.text.primary, fontWeight: 500 }}
          >
            {removeLeadingZeros(transaction.card_num_2) || 'N/A'}
          </Typography>
        </Box>
      </Grid>

      <Grid item xs={12}>
        <Box>
          <Typography
            variant="caption"
            sx={{
              color: theme.palette.text.secondary,
              fontWeight: 600,
              display: 'block',
              mb: 0.5,
            }}
          >
            Vehicle Number
          </Typography>
          <EntityValue
            value={getVehicleDisplayValue(transaction)}
            entityId={getRelatedEntityId(transaction.vehicle_id ?? transaction.vehicle)}
            entityType="vehicle"
            onOpen={onEntityClick}
            sx={{ color: theme.palette.text.primary }}
          />
        </Box>
      </Grid>

      <Grid item xs={6} sm={4}>
        <Box>
          <Typography
            variant="caption"
            sx={{
              color: theme.palette.text.secondary,
              fontWeight: 600,
              display: 'block',
              mb: 0.5,
            }}
          >
            Vehicle Model
          </Typography>
          <Typography
            variant="body2"
            sx={{ color: theme.palette.text.primary, fontWeight: 500 }}
          >
            {transaction.vehicle_model || 'N/A'}
          </Typography>
        </Box>
      </Grid>

      <Grid item xs={6} sm={4}>
        <Box>
          <Typography
            variant="caption"
            sx={{
              color: theme.palette.text.secondary,
              fontWeight: 600,
              display: 'block',
              mb: 0.5,
            }}
          >
            Vehicle Category
          </Typography>
          <Typography
            variant="body2"
            sx={{ color: theme.palette.text.primary, fontWeight: 500 }}
          >
            {transaction.vehicle_category || 'N/A'}
          </Typography>
        </Box>
      </Grid>

      <Grid item xs={12} sm={6}>
        <Box>
          <Typography
            variant="caption"
            sx={{
              color: theme.palette.text.secondary,
              fontWeight: 600,
              display: 'block',
              mb: 0.5,
            }}
          >
            Odometer Reading
          </Typography>
          <Typography
            variant="body2"
            sx={{ color: theme.palette.text.primary, fontWeight: 500 }}
          >
            {transaction.odometer_entry ?? 'N/A'}
          </Typography>
        </Box>
      </Grid>
    </Grid>

    {/* Quantity Badge */}
    <Box
      sx={{
        mb: 2,
        p: 2,
        borderRadius: 2,
        bgcolor: VEHICLE_STYLE_COLORS.selected,
        textAlign: 'center',
      }}
    >
      <Typography
        variant="caption"
        sx={{
          color: theme.palette.text.secondary,
          fontWeight: 600,
          display: 'block',
          mb: 0.5,
        }}
      >
        Quantity Dispensed
      </Typography>
      <Typography
        variant="h6"
        sx={{
          fontWeight: 700,
          color: VEHICLE_STYLE_COLORS.primary,
          fontSize: { xs: '1.125rem', sm: '1.25rem' },
        }}
      >
        {getFuelQuantity(transaction.quantity, transaction.quantity_gallons) || '0 L'}
      </Typography>
    </Box>

    {/* Action Buttons */}
    <Box sx={{ display: 'flex', gap: 1.5, mt: 2 }}>
      <Button
        variant="outlined"
        fullWidth
        disabled={disabled}
        startIcon={<VisibilityOutlinedIcon />}
        sx={{
          borderRadius: 2,
          textTransform: 'none',
          py: { xs: 1, sm: 1.25 },
          fontSize: { xs: '0.875rem', sm: '0.938rem' },
          fontWeight: 600,
          borderColor: 'hsl(198 87% 32%)',
          color: 'hsl(198 87% 32%)',
          '&:hover': {
            borderColor: 'hsl(198 87% 32%)',
            backgroundColor: 'rgba(11, 106, 153, 0.06)',
          },
        }}
        onClick={() => onViewClick(transaction)}
      >
        View
      </Button>
      {canEdit && (
        <Button
          variant="contained"
          fullWidth
          disabled={disabled}
          startIcon={<EditOutlinedIcon />}
          sx={{
            borderRadius: 2,
            textTransform: 'none',
            py: { xs: 1, sm: 1.25 },
            fontSize: { xs: '0.875rem', sm: '0.938rem' },
            fontWeight: 600,
            backgroundColor: 'hsl(198 87% 32%)',
            borderColor: 'hsl(198 87% 32%)',
            boxShadow: '0 2px 8px rgba(0,0,0,0.15)',
            '&:hover': {
              backgroundColor: 'hsl(198 87% 32%)',
              borderColor: 'hsl(198 87% 32%)',
              filter: 'brightness(0.95)',
              boxShadow: '0 4px 12px rgba(0,0,0,0.2)',
            },
          }}
          onClick={() => onEditClick(transaction)}
        >
          Edit
        </Button>
      )}
    </Box>
  </Card>
));

SalesMobileCard.displayName = 'SalesMobileCard';

SalesMobileCard.propTypes = {
  transaction: PropTypes.object.isRequired,
  onViewClick: PropTypes.func.isRequired,
  onEditClick: PropTypes.func.isRequired,
  onEntityClick: PropTypes.func.isRequired,
  theme: PropTypes.object.isRequired,
  disabled: PropTypes.bool,
  canEdit: PropTypes.bool,
};

const TransactionListTable = ({
  isSales,
  refreshData,
  forceRefresh = false,
  refreshRequestId = 0,
  onRefreshStateChange,
  searchText,
  multiSearch = {},
  dateFilter,
  siteFilter,
  tankFilter,
  transactionTypeOverride,
  columnPreferences,
}) => {
  const theme = useTheme();
  const history = useHistory();
  const dispatch = useDispatch();
  const { getUser } = useAuth();
  const user = getUser();
  const hasAdminAccess = hasAdminLikeAccess(getUser);
  const isApprover = user?.role === USER_ROLES.approver;
  const {
    transactionList,
    transactionType,
    totalCount,
    activeListCacheKey,
    listLoading,
    listRefreshing,
  } = useSelector((state) => state.transactions);
  const { appSettings } = useSelector((state) => state.settings);
  const { allVehicles } = useSelector((state) => state.vehicles);
  const { allDispensers } = useSelector((state) => state.dispensers);
  const effectiveTransactionType = transactionTypeOverride || transactionType;

  // Responsive breakpoints
  const isMobile = useMediaQuery(theme.breakpoints.down('md')); // < 900px
  const isTablet = useMediaQuery(theme.breakpoints.between('sm', 'md')); // 600px - 900px

  const [limit, setLimit] = useState(25);
  const [page, setPage] = useState(0);
  const [order, setOrder] = useState('desc');
  const [orderBy, setOrderBy] = useState('order_id');
  const [entityDialog, setEntityDialog] = useState(null);
  const [editingIssuedTransaction, setEditingIssuedTransaction] = useState(null);
  const [editingInwardTransaction, setEditingInwardTransaction] = useState(null);
  const [editingExternalTransaction, setEditingExternalTransaction] = useState(null);

  useEffect(() => {
    if (effectiveTransactionType !== TRANSACTION_TYPES[1].id) {
      if (!allVehicles.length) dispatch(getAllVehicles());
      if (!allDispensers.length) dispatch(getAllDispensers());
    }
  }, [allDispensers.length, allVehicles.length, dispatch, effectiveTransactionType]);

  const vehicleIdByNumber = useMemo(() => (
    new Map(
      (allVehicles || [])
        .filter((vehicle) => vehicle.registration_num && vehicle.id !== undefined)
        .map((vehicle) => [String(vehicle.registration_num).trim().toUpperCase(), vehicle.id])
    )
  ), [allVehicles]);

  const dispenserIdByAddress = useMemo(() => (
    new Map(
      (allDispensers || [])
        .filter((dispenser) => dispenser.address && dispenser.id !== undefined)
        .map((dispenser) => [String(dispenser.address).trim().toUpperCase(), dispenser.id])
    )
  ), [allDispensers]);

  const getTransactionVehicleId = (transaction = {}) => (
    getRelatedEntityId(transaction.vehicle_id ?? transaction.vehicle)
    ?? vehicleIdByNumber.get(String(getVehicleDisplayValue(transaction)).trim().toUpperCase())
  );

  const getTransactionDispenserId = (transaction = {}) => (
    getRelatedEntityId(transaction.dispenser_id ?? transaction.dispenser)
    ?? dispenserIdByAddress.get(String(
      transaction.address ?? transaction.dispenser_address ?? ''
    ).trim().toUpperCase())
  );

  const handleViewTransactionDetails = (transaction) => {
    const transactionId = transaction?.id || transaction?.order_id;
    if (!transactionId) return;
    const returnSearch = history.location?.search || `?type=${effectiveTransactionType}`;
    history.push({
      pathname: `${ROUTE.transactions}/${transactionId}`,
      search: `?type=${effectiveTransactionType}&mode=view`,
      state: {
        transaction,
        returnTo: `${ROUTE.transactions}${returnSearch}`
      }
    });
  };
  const activeTransactionHeaders = useMemo(
    () => transactionTableHeaders[effectiveTransactionType] || [],
    [effectiveTransactionType]
  );
  const ownColumnPreferences = useTableColumnPreferences(
    `transactions_${effectiveTransactionType}`,
    activeTransactionHeaders,
    !columnPreferences
  );
  const {
    visibleColumns: visibleTransactionHeaders
  } = columnPreferences || ownColumnPreferences;
  const transactionTableMinWidth = useMemo(
    () => getTransactionTableMinWidth(visibleTransactionHeaders, isTablet),
    [isTablet, visibleTransactionHeaders]
  );
  const lastRefreshRequestId = useRef(0);

  useEffect(() => {
    const orderById = effectiveTransactionType === TRANSACTION_TYPES[0].id ? 'order_id' : 'id';
    setOrderBy(orderById);
  }, [effectiveTransactionType]);

  const buildListRequest = useCallback((pageNo = 1, shouldForceRefresh = false) => {
    const params = getTransactionQueryParams(
      isSales,
      searchText,
      dateFilter,
      siteFilter,
      effectiveTransactionType,
      tankFilter,
      multiSearch
    );
    const cacheKey = JSON.stringify({
      transactionType: effectiveTransactionType,
      limit,
      pageNo,
      params,
    });
    return {
      transactionType: effectiveTransactionType,
      limit,
      pageNo,
      params,
      cacheKey,
      forceRefresh: shouldForceRefresh,
    };
  }, [
    dateFilter,
    effectiveTransactionType,
    isSales,
    limit,
    searchText,
    multiSearch,
    siteFilter,
    tankFilter,
  ]);

  useEffect(() => {
    setPage(0);
    dispatch(getTransactionList(buildListRequest(1, forceRefresh)));
  }, [
    dispatch,
    effectiveTransactionType,
    limit,
    refreshData,
    isSales,
    searchText,
    dateFilter,
    siteFilter,
    tankFilter,
    buildListRequest,
    forceRefresh
  ]);

  useEffect(() => {
    if (!refreshRequestId || refreshRequestId === lastRefreshRequestId.current) return undefined;
    lastRefreshRequestId.current = refreshRequestId;
    onRefreshStateChange?.(true);
    const request = dispatch(getTransactionList(buildListRequest(page + 1, true)));
    request.finally(() => onRefreshStateChange?.(false));
    return undefined;
  }, [
    buildListRequest,
    dispatch,
    onRefreshStateChange,
    page,
    refreshRequestId
  ]);

  const handleLimitChange = (event) => {
    const newLimit = parseInt(event.target.value, 10);
    setPage(0);
    setLimit(newLimit);
  };

  const handlePageChange = (e, newPage) => {
    setPage(newPage);
    dispatch(getTransactionList(buildListRequest(newPage + 1)));
  };

  const refetchCurrentTableData = () => {
    dispatch(getTransactionList(buildListRequest(page + 1, true)));
  };

  const activeRequestKey = buildListRequest(page + 1).cacheKey;
  const hasActiveList = activeListCacheKey === activeRequestKey;
  const visibleTransactionList = hasActiveList ? transactionList : [];
  const visibleTotalCount = hasActiveList ? totalCount : 0;

  const isDetailsDisabled = () => {
    if (effectiveTransactionType === TRANSACTION_TYPES[2].id) {
      return !hasAdminAccess;
    }
    if (effectiveTransactionType === TRANSACTION_TYPES[0].id) {
      return !hasAdminAccess;
    }
    if (effectiveTransactionType === TRANSACTION_TYPES[1].id) {
      return !(hasAdminAccess || isApprover);
    }
    if (effectiveTransactionType === TRANSACTION_TYPES[3].id) {
      return !hasAdminAccess;
    }
    return true;
  };

  const canEditTransaction = (() => {
    if (effectiveTransactionType === TRANSACTION_TYPES[0].id) {
      return hasAdminAccess;
    }
    if (effectiveTransactionType === TRANSACTION_TYPES[1].id) {
      return hasAdminAccess || isApprover;
    }
    if (effectiveTransactionType === TRANSACTION_TYPES[3].id) {
      return hasAdminAccess;
    }
    return false;
  })();

  const handleEditTransaction = async (transactionOrId) => {
    if (effectiveTransactionType === TRANSACTION_TYPES[2].id || isDetailsDisabled()) {
      return;
    }
    const transaction = typeof transactionOrId === 'object' && transactionOrId !== null
      ? transactionOrId
      : (visibleTransactionList || []).find((t) => (t.id === transactionOrId || t.order_id === transactionOrId)) || { id: transactionOrId };

    const txId = transaction.id || transaction.order_id;

    if (effectiveTransactionType === TRANSACTION_TYPES[0].id) {
      setEditingIssuedTransaction(transaction);
      APIService.getTransactionDetails(effectiveTransactionType, txId)
        .then((res) => {
          if (res?.result) {
            setEditingIssuedTransaction((prev) => (prev ? { ...prev, ...res.result } : prev));
          }
        })
        .catch(() => {});
    } else if (effectiveTransactionType === TRANSACTION_TYPES[1].id) {
      setEditingInwardTransaction(transaction);
      try {
        const res = await APIService.getTransactionDetails(effectiveTransactionType, txId);
        if (res?.result) {
          setEditingInwardTransaction(res.result);
        }
      } catch (err) {
        // Fallback to row transaction
      }
    } else if (effectiveTransactionType === TRANSACTION_TYPES[3].id) {
      setEditingExternalTransaction(transaction);
      try {
        const res = await APIService.getTransactionDetails(effectiveTransactionType, txId);
        if (res?.result) {
          setEditingExternalTransaction(res.result);
        }
      } catch (err) {
        // Fallback to row transaction
      }
    }
  };

  const openEntityDialog = (entityType, entityId, label) => {
    if (entityId === null || entityId === undefined) return;
    setEntityDialog({ entityType, entityId, label });
  };

  const closeEntityDialog = () => setEntityDialog(null);

  const handleRequestSort = (e, property) => {
    const isAsc = orderBy === property && order === 'asc';
    setOrder(isAsc ? 'desc' : 'asc');
    setOrderBy(property);
  };

  const renderTableData = (transaction, rowIndex) => {
    const isTransferType = effectiveTransactionType === TRANSACTION_TYPES[2].id;
    const renderQuantity = (value) => (
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <Typography
          variant="body1"
          sx={{
            fontWeight: 600,
            color: theme.palette.text.primary,
            fontSize: { sm: '0.875rem', md: '0.938rem' },
          }}
        >
          {getFuelQuantity(value, transaction.quantity_gallons)}
        </Typography>
      </Box>
    );

    const getCellValue = (columnId) => {
      if (columnId === 'serial_number') return (page * limit) + rowIndex + 1;
      if (effectiveTransactionType === TRANSACTION_TYPES[1].id) {
        const inwardValues = {
          id: transaction.id,
          order_date: getFormattedDate(transaction.order_date),
          delivery_date: getFormattedDate(transaction.delivery_date),
          site_name: (
            <EntityValue
              value={transaction.site_name}
              entityId={getRelatedEntityId(transaction.site_id ?? transaction.site)}
              entityType="site"
              onOpen={openEntityDialog}
            />
          ),
          invoice_number: transaction.invoice_number,
          quantity: renderQuantity(transaction.actual_filled || transaction.quantity),
          price_per_litre: transaction.price_per_litre,
          supplier: transaction.supplier,
          source: transaction.source === 'sensor'
            ? `${transaction.source_provider || 'Sensor'}`
            : 'Manual',
          received_tank_name: transaction.received_tank_name || 'N/A'
        };
        return inwardValues[columnId] ?? transaction[columnId] ?? 'N/A';
      }

      if (effectiveTransactionType === TRANSACTION_TYPES[3].id) {
        const externalValues = {
          id: transaction.id,
          date_time: getFormattedDate(transaction.date_time),
          site_name: (
            <EntityValue
              value={transaction.site_name}
              entityId={getRelatedEntityId(transaction.site_id ?? transaction.site)}
              entityType="site"
              onOpen={openEntityDialog}
            />
          ),
          dispenser_name: (
            <EntityValue
              value={transaction.dispenser_name}
              entityId={transaction.dispenser_id}
              entityType="dispenser"
              onOpen={openEntityDialog}
            />
          ),
          vehicle_number: (
            <EntityValue
              value={getVehicleDisplayValue(transaction)}
              entityId={getRelatedEntityId(transaction.vehicle_id ?? transaction.vehicle)}
              entityType="vehicle"
              onOpen={openEntityDialog}
            />
          ),
          odometer_entry: transaction.odometer_entry ?? 'N/A',
          quantity: getFuelQuantity(Number(transaction.quantity || 0), transaction.quantity_gallons),
          unit_price: transaction.unit_price ?? 'N/A',
          amount: transaction.amount ?? 'N/A'
        };
        return externalValues[columnId] ?? transaction[columnId] ?? 'N/A';
      }

      const transactionValues = {
        order_id: transaction.order_id,
        end_time: getFormattedDate(transaction.start_time),
        site_name: (
          <EntityValue
            value={transaction.site_name}
            entityId={getRelatedEntityId(transaction.site_id ?? transaction.site)}
            entityType="site"
            onOpen={openEntityDialog}
          />
        ),
        address: (
          <EntityValue
            value={getDispenserDisplayValue(transaction)}
            entityId={getRelatedEntityId(transaction.dispenser_id ?? transaction.dispenser)}
            entityType="dispenser"
            onOpen={openEntityDialog}
          />
        ),
        nozzle_num: transaction.nozzle_num,
        card_num_1: isTransferType ? removeLeadingZeros(transaction.card_num_1) || 'N/A' : transaction.card_num_1,
        card_num_2: isTransferType ? removeLeadingZeros(transaction.card_num_2) || 'N/A' : transaction.card_num_2,
        vehicle_number: (
          <EntityValue
            value={getVehicleDisplayValue(transaction)}
            entityId={getRelatedEntityId(transaction.vehicle_id ?? transaction.vehicle)}
            entityType="vehicle"
            onOpen={openEntityDialog}
          />
        ),
        vehicle_model: transaction.vehicle_model || 'N/A',
        vehicle_category: transaction.vehicle_category || 'N/A',
        odometer_entry: transaction.odometer_entry ?? 'N/A',
        quantity: renderQuantity(transaction.quantity),
        opening_stock: transaction.opening_stock == null
          ? 'None'
          : getFuelQuantity(transaction.opening_stock, transaction.opening_stock_gallons),
        closing_stock: transaction.closing_stock == null
          ? 'None'
          : getFuelQuantity(transaction.closing_stock, transaction.closing_stock_gallons)
      };
      return transactionValues[columnId] ?? transaction[columnId] ?? 'N/A';
    };

    return visibleTransactionHeaders
      .filter((column) => column.id !== 'action')
      .map((column) => (
        <TableCell
          align="center"
          key={column.id}
          sx={{
            fontSize: { sm: '0.813rem', md: '0.875rem' },
            ...(column.id === 'serial_number' ? {
              minWidth: `${TRANSACTION_COLUMN_WIDTHS.serial_number}px !important`,
              width: `${TRANSACTION_COLUMN_WIDTHS.serial_number}px !important`,
              px: 0.75
            } : {}),
            ...(column.id === 'site_name' ? {
              px: 0,
              textAlign: 'center'
            } : {}),
            '& > *': {
              width: '100%',
              textAlign: 'center !important'
            }
          }}
        >
          {getCellValue(column.id)}
        </TableCell>
      ));
  };

  // Get sorted data
  const displayData = (visibleTransactionList?.slice().sort(getComparator(order, orderBy)) || []).map((transaction) => {
    if (effectiveTransactionType === TRANSACTION_TYPES[1].id) return transaction;
    return {
      ...transaction,
      vehicle_id: transaction.vehicle_id ?? getTransactionVehicleId(transaction),
      dispenser_id: transaction.dispenser_id ?? getTransactionDispenserId(transaction)
    };
  });

  // Determine which mobile card to use
  const isPurchaseType = effectiveTransactionType === TRANSACTION_TYPES[1].id;
  const hasActiveFilter = Boolean(
    dateFilter
    || (siteFilter && siteFilter !== 'all')
    || (tankFilter && tankFilter !== 'all')
  );
  const emptyStateMessage = hasActiveFilter
    ? 'No transactions found for the selected filters'
    : 'No transactions found';

  return (
    <>
      {listLoading && !visibleTransactionList?.length ? (
        <Loader />
      ) : (
        <>
          {listRefreshing && (
            <Typography variant="caption" sx={{ display: 'block', textAlign: 'right', color: 'text.secondary' }}>
              Refreshing transactions…
            </Typography>
          )}
          {/* Mobile View: Card Layout */}
          {isMobile ? (
            <Box sx={{ width: '100%' }}>
              {displayData.length > 0 ? (
                displayData.map((transaction) => (isPurchaseType ? (
                  <PurchaseMobileCard
                    key={transaction.id}
                    transaction={transaction}
                    onViewClick={handleViewTransactionDetails}
                    onEditClick={handleEditTransaction}
                    onEntityClick={openEntityDialog}
                    theme={theme}
                    disabled={isDetailsDisabled()}
                    canEdit={canEditTransaction}
                  />
                ) : (
                  <SalesMobileCard
                    key={transaction.id || transaction.order_id}
                    transaction={transaction}
                    onViewClick={handleViewTransactionDetails}
                    onEditClick={handleEditTransaction}
                    onEntityClick={openEntityDialog}
                    theme={theme}
                    disabled={isDetailsDisabled()}
                    canEdit={canEditTransaction}
                  />
                )))
              ) : (
                <Card sx={{ p: 3, textAlign: 'center' }}>
                  <Typography variant="body1" color="text.secondary">
                    {emptyStateMessage}
                  </Typography>
                </Card>
              )}
              <TransactionPagination
                page={page}
                pageSize={limit}
                count={visibleTotalCount}
                onPageChange={handlePageChange}
                onPageSizeChange={handleLimitChange}
              />
            </Box>
          ) : (
            /* Desktop/Tablet View: Table Layout */
            <Card
              sx={{
                borderRadius: 4,
                background: '#FFFFFF',
                border: `1px solid ${VEHICLE_STYLE_COLORS.border}`,
                height: 'auto',
                display: 'flex',
                flexDirection: 'column',
                overflow: 'visible'
              }}
            >
              <Box
                sx={{
                  minHeight: 0,
                  overflowX: 'auto',
                  overflowY: 'visible',
                  scrollbarWidth: 'thin',
                  scrollbarColor: 'rgba(100, 116, 139, 0.28) transparent',
                  '&::-webkit-scrollbar': {
                    width: 6,
                    height: 6
                  },
                  '&::-webkit-scrollbar-track': {
                    background: 'transparent'
                  },
                  '&::-webkit-scrollbar-thumb': {
                    backgroundColor: 'rgba(100, 116, 139, 0.28)',
                    borderRadius: '9999px'
                  },
                  '&::-webkit-scrollbar-thumb:hover': {
                    backgroundColor: 'rgba(100, 116, 139, 0.45)'
                  },
                  '& .MuiTableCell-stickyHeader': {
                    backgroundColor: VEHICLE_STYLE_COLORS.white,
                    borderBottom: `1px solid ${VEHICLE_STYLE_COLORS.border}`,
                    zIndex: 2
                  },
                  '& .MuiTableBody-root .MuiTableCell-root': {
                    color: VEHICLE_STYLE_COLORS.foreground,
                    borderBottom: `1px solid ${VEHICLE_STYLE_COLORS.border}`
                  },
                  '& .MuiTableBody-root .MuiTableCell-root:not(:last-child)': {
                    overflow: 'hidden',
                    textOverflow: 'ellipsis'
                  },
                  '& .MuiTableCell-root': {
                    minWidth: DEFAULT_TRANSACTION_COLUMN_WIDTH,
                    whiteSpace: 'nowrap'
                  },
                  '& .MuiTableHead-root .MuiTableCell-root:last-of-type': {
                    position: 'sticky',
                    right: 0,
                    minWidth: TRANSACTION_COLUMN_WIDTHS.action,
                    width: TRANSACTION_COLUMN_WIDTHS.action,
                    textAlign: 'center',
                    zIndex: 3,
                    backgroundColor: VEHICLE_STYLE_COLORS.white,
                    boxShadow: '-1px 0 0 rgba(0, 0, 0, 0.08)'
                  }
                }}
              >
                <Box sx={{ minWidth: transactionTableMinWidth }}>
                  <Table stickyHeader sx={{ tableLayout: 'auto', minWidth: transactionTableMinWidth }}>
                    <EnhancedTableHead
                      order={order}
                      orderBy={orderBy}
                      headCells={visibleTransactionHeaders}
                      onRequestSort={handleRequestSort}
                      sx={{
                        backgroundColor: VEHICLE_STYLE_COLORS.white,
                        borderBottom: `1px solid ${VEHICLE_STYLE_COLORS.border}`,
                        '& .MuiTableCell-root': {
                          color: VEHICLE_STYLE_COLORS.foreground,
                          backgroundColor: VEHICLE_STYLE_COLORS.white,
                          borderBottom: `1px solid ${VEHICLE_STYLE_COLORS.border}`,
                          '&:hover': { color: VEHICLE_STYLE_COLORS.foreground }
                        },
                        '& .MuiTableSortLabel-root': {
                          color: `${VEHICLE_STYLE_COLORS.foreground} !important`,
                          '&:hover': { color: `${VEHICLE_STYLE_COLORS.foreground} !important` }
                        },
                        '& .MuiTableSortLabel-root.Mui-active': {
                          color: `${VEHICLE_STYLE_COLORS.primary} !important`
                        },
                        '& .MuiTableSortLabel-root .MuiTableSortLabel-icon': {
                          color: `${VEHICLE_STYLE_COLORS.muted} !important`
                        },
                        '& .MuiTableSortLabel-root.Mui-active .MuiTableSortLabel-icon': {
                          color: `${VEHICLE_STYLE_COLORS.primary} !important`
                        }
                      }}
                    />
                    <TableBody>
                      {displayData.length > 0 ? (
                        displayData.map((transaction, rowIndex) => {
                          const isStripedRow = rowIndex % 2 === 0;
                          const rowBackground = isStripedRow
                            ? '#eaf3f6'
                            : VEHICLE_STYLE_COLORS.white;
                          const rowHoverBackground = isStripedRow
                            ? '#dcecf2'
                            : VEHICLE_STYLE_COLORS.rowHover;

                          return (
                            <TableRow
                              hover
                              key={transaction.id}
                              sx={{
                                backgroundColor: rowBackground,
                                transition: 'background-color 0.3s, box-shadow 0.3s',
                                '& > .MuiTableCell-root': {
                                  backgroundColor: rowBackground
                                },
                                '&:hover': {
                                  backgroundColor: rowHoverBackground,
                                  boxShadow: theme.shadows[2],
                                  '& > .MuiTableCell-root': {
                                    backgroundColor: rowHoverBackground
                                  }
                                },
                              }}
                            >
                              {renderTableData(transaction, rowIndex)}
                              <TableCell
                                align="center"
                                sx={{
                                  position: 'sticky',
                                  right: 0,
                                  minWidth: TRANSACTION_COLUMN_WIDTHS.action,
                                  width: TRANSACTION_COLUMN_WIDTHS.action,
                                  zIndex: 4,
                                  backgroundColor: rowBackground,
                                  isolation: 'isolate',
                                  'tr:hover &': {
                                    backgroundColor: rowHoverBackground
                                  },
                                  boxShadow: '-2px 0 0 rgba(15, 23, 42, 0.08)',
                                  overflow: 'hidden'
                                }}
                              >
                                <Stack
                                  direction="row"
                                  spacing={1}
                                  justifyContent="center"
                                  alignItems="center"
                                  sx={{ flexWrap: 'nowrap' }}
                                >
                                  <Tooltip title="View Details">
                                    <IconButton
                                      onClick={(e) => {
                                        e.stopPropagation();
                                        handleViewTransactionDetails(transaction);
                                      }}
                                      disabled={isDetailsDisabled()}
                                      size="medium"
                                      aria-label="View details"
                                      sx={getTransactionSymbolSx()}
                                    >
                                      <VisibilityOutlinedIcon sx={TABLE_ACTION_ICON_SX} />
                                    </IconButton>
                                  </Tooltip>
                                  {canEditTransaction && (
                                  <Tooltip title="Edit Transaction">
                                    <span>
                                      <IconButton
                                        onClick={(e) => {
                                          e.stopPropagation();
                                          handleEditTransaction(transaction);
                                        }}
                                        disabled={isDetailsDisabled()}
                                        size="medium"
                                        aria-label="Edit transaction"
                                        sx={getTransactionSymbolSx()}
                                      >
                                        <EditOutlinedIcon sx={TABLE_ACTION_ICON_SX} />
                                      </IconButton>
                                    </span>
                                  </Tooltip>
                                  )}
                                </Stack>
                              </TableCell>
                            </TableRow>
                          );
                        })
                      ) : (
                        <TableRow>
                          <TableCell
                            colSpan={visibleTransactionHeaders.length || 1}
                            align="center"
                            sx={{ py: 4 }}
                          >
                            <Typography variant="body1" color="text.secondary">
                              {emptyStateMessage}
                            </Typography>
                          </TableCell>
                        </TableRow>
                      )}
                    </TableBody>
                  </Table>
                </Box>
              </Box>
              <Box sx={{ bgcolor: '#FFFFFF' }}>
                <TransactionPagination
                  page={page}
                  pageSize={limit}
                  count={visibleTotalCount}
                  onPageChange={handlePageChange}
                  onPageSizeChange={handleLimitChange}
                />
              </Box>
            </Card>
          )}
        </>
      )}

      {entityDialog?.entityType === 'site' && (
        <SiteDetailsDialog
          open
          onClose={closeEntityDialog}
          siteId={entityDialog.entityId}
          siteName={entityDialog.label}
        />
      )}
      {entityDialog?.entityType === 'vehicle' && (
        <VehicleDetailsDialog
          open
          onClose={closeEntityDialog}
          vehicleId={entityDialog.entityId}
          vehicleNumber={entityDialog.label}
        />
      )}
      {entityDialog?.entityType === 'dispenser' && (
        <DispenserDetailsDialog
          open
          onClose={closeEntityDialog}
          dispenserId={entityDialog.entityId}
          dispenserLabel={entityDialog.label}
        />
      )}
      {/* UPDATE ODOMETER (ISSUED / OUTWARD) */}
      {Boolean(editingIssuedTransaction) && (
        <UpdateOdometerModal
          open={Boolean(editingIssuedTransaction)}
          transactionId={Number(editingIssuedTransaction.id || editingIssuedTransaction.order_id)}
          currentValue={editingIssuedTransaction.odometer_entry}
          vehicleNumber={editingIssuedTransaction.vehicle_number}
          siteId={editingIssuedTransaction.site_id ?? editingIssuedTransaction.site ?? null}
          additionalEntry={editingIssuedTransaction.additional_entry}
          assetCodeMappingEnabled={Boolean(appSettings?.asset_code_mapping_enabled)}
          onSuccess={() => {
            setEditingIssuedTransaction(null);
            refetchCurrentTableData();
            dispatch(getUnreadNotificationsCount());
          }}
          onClose={() => setEditingIssuedTransaction(null)}
        />
      )}

      {/* UPDATE INWARD TRANSACTION */}
      {Boolean(editingInwardTransaction) && (
        <NewTransaction
          open={Boolean(editingInwardTransaction)}
          initialValues={editingInwardTransaction}
          transactionType={TRANSACTION_TYPES[1].id}
          handleClose={() => {
            setEditingInwardTransaction(null);
            refetchCurrentTableData();
          }}
        />
      )}

      {/* UPDATE EXTERNAL TRANSACTION */}
      {Boolean(editingExternalTransaction) && (
        <NewTransaction
          open={Boolean(editingExternalTransaction)}
          initialValues={editingExternalTransaction}
          transactionType={TRANSACTION_TYPES[3].id}
          handleClose={() => {
            setEditingExternalTransaction(null);
            refetchCurrentTableData();
          }}
        />
      )}
    </>
  );
};

TransactionListTable.propTypes = {
  refreshData: PropTypes.bool,
  forceRefresh: PropTypes.bool,
  refreshRequestId: PropTypes.number,
  onRefreshStateChange: PropTypes.func,
  isSales: PropTypes.bool,
  searchText: PropTypes.string,
  multiSearch: PropTypes.object,
  dateFilter: PropTypes.oneOfType([
    PropTypes.string,
    PropTypes.shape({
      start: PropTypes.string,
      end: PropTypes.string
    })
  ]),
  siteFilter: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  tankFilter: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  transactionTypeOverride: PropTypes.string,
  columnPreferences: PropTypes.shape({
    hiddenColumnIds: PropTypes.arrayOf(PropTypes.string),
    visibleColumns: PropTypes.arrayOf(PropTypes.object),
    onToggleColumn: PropTypes.func
  }),
};

export default TransactionListTable;
